import type { ReactNode } from 'react';
import { IonButton, IonIcon, IonSpinner, IonText } from '@ionic/react';
import { alertCircleOutline } from 'ionicons/icons';
import { MarkdownEditor } from '../../../../components/markdown/MarkdownEditor';
import { useAppConfig } from '../../../../hooks/useAppConfig';
import { getPostLinkError } from '../utils/postLink';
import { PostLinkFields } from './PostLinkFields';

interface PostComposerProps {
  value: string;
  onChange: (value: string) => void;
  // Optional "Enlace relacionado" (URL + optional title).
  linkUrl: string;
  linkTitle: string;
  onLinkUrlChange: (value: string) => void;
  onLinkTitleChange: (value: string) => void;
  onSaveDraft: () => void;
  onPublish: () => void;
  savingDraft: boolean;
  publishing: boolean;
  errorMessage?: string;
  // Whether to offer the "Guardar borrador" action. Editing an already-published
  // post only allows re-publishing, never turning it back into a draft.
  allowDraft?: boolean;
  // When provided, a destructive "Eliminar publicación" action is shown below
  // "Publicar" (used to delete a draft from its editor).
  onDelete?: () => void;
  deleting?: boolean;
  // Photos section, rendered under the related link (the editor wires its data).
  photos?: ReactNode;
}

// Pure form for writing a post: a markdown editor, the optional related link,
// the photos slot and "Guardar borrador" / "Publicar" actions. No data fetching, so it stays trivial to unit test.
export const PostComposer = ({
  value,
  onChange,
  linkUrl,
  linkTitle,
  onLinkUrlChange,
  onLinkTitleChange,
  onSaveDraft,
  onPublish,
  savingDraft,
  publishing,
  errorMessage,
  allowDraft = true,
  onDelete,
  deleting = false,
  photos,
}: PostComposerProps) => {
  const { postMaxTextLength } = useAppConfig();
  const busy = savingDraft || publishing || deleting;
  const isBlank = value.trim().length === 0;
  // The link rules apply to drafts too, so an invalid link blocks both actions.
  const linkError = getPostLinkError({ url: linkUrl, title: linkTitle });

  return (
    <div className="flex flex-col gap-4">
      <MarkdownEditor
        value={value}
        onChange={onChange}
        maxLength={postMaxTextLength}
        placeholder="Escribe tu publicación"
        disabled={busy}
      />

      <PostLinkFields
        url={linkUrl}
        title={linkTitle}
        onUrlChange={onLinkUrlChange}
        onTitleChange={onLinkTitleChange}
        error={linkError}
        disabled={busy}
      />

      {photos}

      {errorMessage && (
        <IonText color="danger" className="block">
          <p className="flex items-center gap-1.5 text-sm">
            <IonIcon icon={alertCircleOutline} aria-hidden="true" />
            {errorMessage}
          </p>
        </IonText>
      )}

      <div className="flex flex-col gap-2">
        {allowDraft && (
          <IonButton expand="block" fill="outline" onClick={onSaveDraft} disabled={busy || !!linkError}>
            {savingDraft ? <IonSpinner name="crescent" /> : 'Guardar borrador'}
          </IonButton>
        )}
        <IonButton expand="block" onClick={onPublish} disabled={busy || isBlank || !!linkError}>
          {publishing ? <IonSpinner name="crescent" /> : 'Publicar'}
        </IonButton>
        {onDelete && (
          <IonButton expand="block" fill="outline" color="danger" onClick={onDelete} disabled={busy}>
            {deleting ? <IonSpinner name="crescent" /> : 'Eliminar borrador'}
          </IonButton>
        )}
      </div>
    </div>
  );
};
